import clsx from 'clsx';
import React from 'react';
import { makePath } from 'utils/routes';
import { Link, useLocation } from 'wouter';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { useBillingTabEnabled } from '../../../hooks/useBillingTabEnabled';

const SETTINGS_NAV_ITEM_CLASS = clsx(
  'group/item flex items-center border-b-[0.5px] border-main-200 text-xmd font-helvetica',
  'pl-[calc(0.5lh+3.5px)] pr-f1 h-f4 w-full'
);

type SettingsNavItemProps = {
  href: string;
  label: string;
  active: boolean;
  disabled?: boolean;
};

const SettingsNavItem = ({
  href,
  label,
  active,
  disabled,
}: SettingsNavItemProps) => (
  <Link href={href}>
    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
    <a
      className={clsx(
        SETTINGS_NAV_ITEM_CLASS,
        disabled && 'text-main-300 cursor-not-allowed',
        !disabled && active && 'text-main bg-main-100',
        !disabled && !active && 'text-main-500 hover:text-main'
      )}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      data-testid={`settings-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="truncate">{label}</span>
    </a>
  </Link>
);

export const PaymentsSettingsNav = () => {
  const [location] = useLocation();
  const { workspace } = useWorkspace();
  const billingTabEnabled = useBillingTabEnabled();
  const isPaymentsWorkspace = workspace?.type === 'payments';
  const workspaceId = workspace?.id ?? '';

  const settingsBase = makePath(['w', workspaceId, 'payments', 'settings']);

  const activeTab = (() => {
    const m = location.match(/\/settings\/(.+)/);
    return m ? m[1] : 'workspace';
  })();

  return (
    <div className="flex flex-col -mx-f1" data-testid="settings-nav">
      <SettingsNavItem
        href={`${settingsBase}/workspace`}
        label="General"
        active={activeTab === 'workspace'}
      />
      <SettingsNavItem
        href={`${settingsBase}/api-clients`}
        label="API Clients"
        active={activeTab === 'api-clients'}
      />
      <SettingsNavItem
        href={`${settingsBase}/members`}
        label="Members"
        active={activeTab === 'members'}
      />
      {!isPaymentsWorkspace && (
        <SettingsNavItem
          href={`${settingsBase}/data-exports`}
          label="Data Exports"
          active={activeTab === 'data-exports'}
        />
      )}
      <SettingsNavItem
        href={`${settingsBase}/billing`}
        label="Billing"
        active={activeTab === 'billing'}
        disabled={!billingTabEnabled}
      />
      <SettingsNavItem
        href={`${settingsBase}/profile`}
        label="Profile"
        active={activeTab === 'profile'}
      />
      <SettingsNavItem
        href={`${settingsBase}/preferences`}
        label="Preferences"
        active={activeTab === 'preferences'}
      />
    </div>
  );
};
